import { useTranslation } from 'react-i18next';
import { Check, Store } from 'lucide-react';
import type { PublishChannel } from '@/api/publishChannels';
import { usePublishStore } from '@/stores/publishStore';
import { cn } from '@/lib/utils';

interface ChannelSelectorProps {
  channels: PublishChannel[];
  /** Channels this listing already sits on; shown, never selectable. */
  publishedChannelIds: string[];
}

/**
 * Lets the seller pick where a listing goes (plan U9).
 *
 * Only rendered when there is something to choose: with one channel, or a
 * channel handed over on the iframe URL, the publish step names the target on
 * the button instead. Channels the listing already sits on are listed as such,
 * because sending again would create a second product.
 */
export function ChannelSelector({
  channels,
  publishedChannelIds,
}: ChannelSelectorProps) {
  const { t } = useTranslation();
  const selected = usePublishStore((state) => state.selectedChannelIds);
  const toggleChannel = usePublishStore((state) => state.toggleChannel);

  return (
    <section className="bg-paper-2 rounded-section border border-line overflow-hidden">
      <div className="p-5 border-b border-line flex items-center gap-3">
        <div className="w-10 h-10 bg-[color-mix(in_srgb,var(--primary)_10%,transparent)] rounded-card flex items-center justify-center">
          <Store className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h2 className="font-semibold text-ink">
            {t('creator.publish.channelsTitle')}
          </h2>
          <p className="text-sm text-mute">
            {t('creator.publish.channelsSubtitle')}
          </p>
        </div>
      </div>

      <ul className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {channels.map((channel) => {
          const alreadyThere = publishedChannelIds.includes(channel.id);
          const isSelected = !alreadyThere && selected.includes(channel.id);

          return (
            <li key={channel.id}>
              <button
                type="button"
                disabled={alreadyThere}
                onClick={() => toggleChannel(channel.id)}
                aria-pressed={isSelected}
                className={cn(
                  'w-full border-2 rounded-card p-4 text-left transition-all',
                  isSelected
                    ? 'border-primary bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]'
                    : 'border-line hover:border-primary',
                  alreadyThere && 'opacity-60 cursor-not-allowed'
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-ink truncate">
                      {channel.displayName}
                    </div>
                    <div className="text-xs text-mute">
                      {alreadyThere
                        ? t('creator.publish.channelAlreadyPublished')
                        : t('creator.publish.channelSelectable')}
                    </div>
                  </div>
                  {isSelected && (
                    <Check
                      className="w-4 h-4 text-primary shrink-0"
                      aria-hidden="true"
                    />
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
